import React, { useState, useContext, useMemo } from 'react';
import { 
  Box, Button, Typography, Card, CardContent, Chip, LinearProgress, useTheme, 
  Tabs, Tab, Divider, Paper, Stack, Container, CardHeader, IconButton, Tooltip, styled 
} from '@mui/material';
import { motion } from 'framer-motion';
import { TestAttempt, SkillName, categories } from '../UserAndProfile/UserContext';
import { PromptContext, getStrategyForSkill } from '../PromptContext';
import { SkillPerformance, MissedQuestionDisplayData, getSkillSection } from './TestResultsUtils';
import {
  AccessTime as Clock, 
  CheckCircle, 
  BarChart, 
  Psychology as Brain, 
  Bolt as Zap, 
  Info, 
  Schedule as Clock3, 
  TrackChanges as Target, 
  MenuBook as BookOpen, 
  HelpOutline as HelpCircle, 
  BarChart as BarChart2,
  Close as CloseIcon
} from '@mui/icons-material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';

interface DemoDiagnosticTestResultsProps {
  testAttempt: TestAttempt;
  onStartTrial: () => void;
  onBackToDemo: () => void;
  onMinimize: () => void;
}

// Custom styled components (matching DiagnosticTestResultsPage)
const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.main}15`,
  borderRadius: "50%",
  padding: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledChip = styled(Chip)<{ difficulty: "Easy" | "Medium" | "Hard" }>(({ difficulty, theme }) => {
  let bgColor = theme.palette.custom.green;
  let textColor = "#fff";

  if (difficulty === "Medium") {
    bgColor = theme.palette.custom.amber;
    textColor = "#fff";
  } else if (difficulty === "Hard") {
    bgColor = theme.palette.custom.red;
  }

  return {
    backgroundColor: bgColor,
    color: textColor,
    fontWeight: 500,
    "&:hover": {
      backgroundColor: bgColor,
    },
  };
});

const BorderLeftCard = styled(Card)<{ bordercolor: string }>(({ bordercolor }) => ({
  borderLeft: `4px solid ${bordercolor}`,
  height: "100%",
}));

const StatsCard = styled(Card)({
  height: "100%",
});

// TabPanel component for the Tabs
function TabPanel(props: {
  children?: React.ReactNode;
  index: number;
  value: number;
}) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const DemoDiagnosticTestResults: React.FC<DemoDiagnosticTestResultsProps> = ({ 
  testAttempt, 
  onStartTrial, 
  onBackToDemo,
  onMinimize 
}) => {
  const theme = useTheme();
  const { renderLatexOrText } = useContext(PromptContext)!;
  const [activeTab, setActiveTab] = useState(0);
  
  // Calculate basic stats
  const allAnswers = testAttempt.sections[0]?.questions || [];
  const totalQuestions = allAnswers.length;
  const correctQuestions = allAnswers.filter(q => q.isCorrect).length;
  const percentCorrect = Math.round((correctQuestions / totalQuestions) * 100);

  // Get estimated score ranges
  const mathScore = testAttempt.mathScore || 0;
  const readingWritingScore = testAttempt.readingWritingScore || 0;
  const totalScore = testAttempt.totalScore || 0;
  const mathScoreRange = testAttempt.diagnosticMathScores || { lower: 520, upper: 680 };
  const readingScoreRange = testAttempt.diagnosticReadingWritingScores || { lower: 530, upper: 660 };

  // Format time data
  const totalTimeMs = testAttempt.sections.reduce(
    (sum: number, section: any) => sum + (section.elapsedTime || 0), 0
  ) || 0;
  const totalMinutes = Math.floor(totalTimeMs / (1000 * 60));
  const totalSeconds = Math.floor((totalTimeMs % (1000 * 60)) / 1000);
  const totalTime = `${totalMinutes}m ${totalSeconds}s`;

  const avgTimeMs = totalQuestions > 0 ? totalTimeMs / totalQuestions : 0;
  const avgMinutes = Math.floor(avgTimeMs / (1000 * 60));
  const avgSeconds = Math.floor((avgTimeMs % (1000 * 60)) / 1000);
  const avgTimePerQuestion = `${avgMinutes}m ${avgSeconds}s`;

  // Format the test date
  const testDate = new Date(testAttempt.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const isMathSkill = (skill: SkillName): boolean => {
    return categories.Math.includes(skill as any);
  };

  // Calculate skill performance (matching DiagnosticTestResultsPage logic)
  const skillPerformance: SkillPerformance[] = useMemo(() => {
    if (!testAttempt || !testAttempt.sections || testAttempt.sections.length === 0) {
      return [];
    }

    const diagnosticSection = testAttempt.sections[0];
    if (!diagnosticSection || !diagnosticSection.questions) {
      return [];
    }

    // Group questions by skill
    const skillMap: Record<string, {
      section: "Math" | "Reading and Writing",
      correct: number,
      total: number,
      totalTime: number,
      questions: number
    }> = {};

    // Process all questions to build skill statistics
    diagnosticSection.questions.forEach(question => {
      const skill = question.skill;
      const section = getSkillSection(skill);
      
      if (!skillMap[skill]) {
        skillMap[skill] = {
          section,
          correct: 0,
          total: 0,
          totalTime: 0,
          questions: 0
        };
      }
      
      skillMap[skill].total++;
      skillMap[skill].questions++;
      
      if (question.isCorrect) {
        skillMap[skill].correct++;
      }
      
      // Add time spent if available
      if (question.timeSpent) {
        skillMap[skill].totalTime += question.timeSpent;
      }
    });

    // Convert the map to the required SkillPerformance array format
    return Object.entries(skillMap).map(([skill, data]) => {
      // Calculate average time in seconds
      const avgTimeInSeconds = data.questions > 0 ? 
        Math.round(data.totalTime / data.questions / 1000) : 0;
      
      // Estimate target time based on section
      const targetTime = data.section === 'Math' ? 80 : 60; 
      
      return {
        skill,
        section: data.section,
        percentCorrect: Math.round((data.correct / data.total) * 100),
        avgTime: avgTimeInSeconds,
        targetTime,
        questionsAttempted: data.total
      };
    }).sort((a, b) => b.percentCorrect - a.percentCorrect); // Sort by performance (high to low)
  }, [testAttempt]);

  // Calculate missed questions (matching DiagnosticTestResultsPage logic)
  const missedQuestions: MissedQuestionDisplayData[] = useMemo(() => {
    if (!testAttempt || !testAttempt.sections || testAttempt.sections.length === 0 || !testAttempt.sections[0].questions) {
      return [];
    }

    const diagnosticSection = testAttempt.sections[0];

    // Filter for incorrect questions
    const incorrectQuestions = diagnosticSection.questions.filter(q => !q.isCorrect);
    
    return incorrectQuestions.map(question => {
      // Format time spent in a readable format
      let timeSpentFormatted = "Unknown";
      if (question.timeSpent) {
        const minutes = Math.floor(question.timeSpent / (1000 * 60));
        const seconds = Math.floor((question.timeSpent % (1000 * 60)) / 1000);
        timeSpentFormatted = `${minutes}m ${seconds}s`;
      }
      
      const skill = question.skill;
      const section = getSkillSection(skill);
      
      return {
        ...question,
        timeSpent: timeSpentFormatted,
        skill: question.skill,
        section,
        difficulty: question.difficulty || 'Medium',
        questionPreview: `Question ${question.questionIndex + 1}`,
        explanation: "This is a demo - full explanations available in the complete program",
        strategy: getStrategyForSkill(question.skill)
      };
    });
  }, [testAttempt]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Helper function to get color based on percentage
  const getColor = (percent: number) => {
    if (percent >= 85) return theme.palette.custom.green;
    if (percent >= 70) return theme.palette.custom.amber;
    return theme.palette.custom.red;
  };

  // Helper function to get time comparison indicator
  const getTimeIndicator = (avgTime: number, targetTime: number) => {
    const diff = avgTime - targetTime;
    if (diff <= 0)
      return (
        <Typography component="span" sx={{ color: theme.palette.custom.green }}>
          ✓
        </Typography>
      );
    if (diff <= 15)
      return (
        <Typography component="span" sx={{ color: theme.palette.custom.amber }}>
          ⚠️
        </Typography>
      );
    return (
      <Typography component="span" sx={{ color: theme.palette.custom.red }}>
        ⏱️
      </Typography>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Minimize Button - Positioned above the component */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Tooltip title="Minimize Results">
            <IconButton 
              onClick={onMinimize}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { md: "center" }, 
            justifyContent: { md: "space-between" }, gap: 2 }}>
            
            <Box sx={{my: 1}}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                🎉 Demo Test Complete!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Demo completed on {testDate} • Here's what you'd see in our full program
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: {xs: 1, md: 0} }}>
              <Box sx={{ textAlign: "center", minWidth: "60px" }}>
                <Typography variant="body2" color="text.secondary">
                  Math
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {mathScore}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center", minWidth: "120px" }}>
                <Typography variant="body2" color="text.secondary">
                  Reading and Writing
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {readingWritingScore}
                </Typography>
              </Box>
              <Typography color="text.secondary">=</Typography>
              <Box sx={{ textAlign: "center", minWidth: "60px", mr: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Estimated Score
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {totalScore}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Quick Stats Cards */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
          {/* Card 1 - Section Scores */}
          <Box sx={{width: {xs: "100%", sm: "calc(50% - 8px)", md: "calc(25% - 12px)" }}}>
            <StatsCard sx={{ height: "100%" }}>
              <CardContent sx={{p: 2, height: "100%", display: "flex", alignItems: "center", gap: 1.5}}>
                <IconWrapper>
                  <BarChart sx={{ color: "primary.main" }} />
                </IconWrapper>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Section Score Range
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.5 }}>
                    <Typography variant="body1" fontWeight="bold">
                      {mathScoreRange.lower}-{mathScoreRange.upper}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Math
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.5 }}>
                    <Typography variant="body1" fontWeight="bold">
                      {readingScoreRange.lower}-{readingScoreRange.upper}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reading
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>

          {/* Card 2 - Overall Score */}
          <Box sx={{width: {xs: "100%", sm: "calc(50% - 8px)", md: "calc(25% - 12px)" }}}>
            <StatsCard sx={{ height: "100%" }}>
              <CardContent sx={{p: 2, height: "100%",display: "flex", alignItems: "center", gap: 1.5}}>
                <IconWrapper>
                  <Target sx={{ color: "primary.main" }} />
                </IconWrapper>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Overall Score
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.5 }}>
                    <Typography variant="h6" fontWeight="bold">{totalScore}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Demo Result
                  </Typography>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>

          {/* Card 3 - Total Time */}
          <Box sx={{width: {xs: "100%", sm: "calc(50% - 8px)", md: "calc(25% - 12px)"}}}>
            <StatsCard sx={{ height: "100%" }}>
              <CardContent sx={{p: 2, pb: 2, height: "100%", display: "flex", alignItems: "center", gap: 1.5}}>
                <IconWrapper>
                  <Clock3 sx={{ color: "primary.main" }} />
                </IconWrapper>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Time
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.5 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {totalTime}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {totalQuestions} Questions
                  </Typography>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>

          {/* Card 4 - Avg Time */}
          <Box sx={{width: {xs: "100%", sm: "calc(50% - 8px)", md: "calc(25% - 12px)"}}}>
            <StatsCard sx={{ height: "100%" }}>
              <CardContent sx={{p: 2, pb: 2, height: "100%", display: "flex", alignItems: "center", gap: 1.5}}>
                <IconWrapper>
                  <Clock sx={{ color: "primary.main" }} />
                </IconWrapper>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Time / Q
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.5 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {avgTimePerQuestion}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Across Test
                  </Typography>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>
        </Box>

        {/* Skill Performance Bar View */}
        <Card sx={{ mb: 4 }}>
          <CardHeader
            title="Skill Performance"
            subheader="Your accuracy and timing across different question types"
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="skill tabs">
                <Tab label="All Skills" />
                <Tab label="Math" />
                <Tab label="Reading and Writing" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <Stack spacing={1.5}>
                {skillPerformance.map((skill, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        width: { xs: 100, sm: 130 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={skill.skill}
                    >
                      {skill.skill}
                    </Typography>
                    <Box sx={{flexGrow: 1, height: 24, bgcolor: "grey.100", borderRadius: 1,
                        overflow: "hidden", position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${skill.percentCorrect}%`,
                          bgcolor: getColor(skill.percentCorrect),
                          borderRadius: 1,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "white", fontWeight: "medium", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                        >
                          {skill.percentCorrect}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "medium" }}>
                          {skill.avgTime}s {getTimeIndicator(skill.avgTime, skill.targetTime)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: "center" }}>
                      {skill.questionsAttempted}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: theme.palette.custom.green }} />
                  <Typography variant="caption" color="text.secondary">
                    85%+ (Strong)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: theme.palette.custom.amber }} />
                  <Typography variant="caption" color="text.secondary">
                    70-84% (Good)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: theme.palette.custom.red }} />
                  <Typography variant="caption" color="text.secondary">
                    Below 70% (Needs Work)
                  </Typography>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Stack spacing={1.5}>
                {skillPerformance
                  .filter((skill) => skill.section === "Math")
                  .map((skill, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{width: { xs: 100, sm: 130 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={skill.skill}>
                        {skill.skill}
                      </Typography>
                      <Box
                        sx={{ flexGrow: 1,
                          height: 24,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${skill.percentCorrect}%`,
                            bgcolor: getColor(skill.percentCorrect),
                            borderRadius: 1,
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "white", fontWeight: "medium", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                          >
                            {skill.percentCorrect}%
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "medium" }}>
                            {skill.avgTime}s {getTimeIndicator(skill.avgTime, skill.targetTime)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: "center" }}>
                        {skill.questionsAttempted}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Stack spacing={1.5}>
                {skillPerformance
                  .filter((skill) => skill.section === "Reading and Writing")
                  .map((skill, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          width: { xs: 100, sm: 130 },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={skill.skill}
                      >
                        {skill.skill}
                      </Typography>
                      <Box
                        sx={{
                          flexGrow: 1,
                          height: 24,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${skill.percentCorrect}%`,
                            bgcolor: getColor(skill.percentCorrect),
                            borderRadius: 1,
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "white", fontWeight: "medium", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                          >
                            {skill.percentCorrect}%
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "medium" }}>
                            {skill.avgTime}s {getTimeIndicator(skill.avgTime, skill.targetTime)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: "center" }}>
                        {skill.questionsAttempted}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </TabPanel>
          </CardContent>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Info sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Numbers on right show questions attempted and average time per question.
            </Typography>
          </Box>
        </Card>

        {/* Next Steps */}
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Next Steps
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unlock your full potential with our comprehensive SAT prep program
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
            {/* Activity 1 */}
            <Box sx={{ flex: 1 }}>
              <BorderLeftCard bordercolor={theme.palette.primary.main}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <BookOpen sx={{ color: "primary.main" }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Review Missed Questions
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Access detailed explanations and strategies for every question you missed
                  </Typography>
                  <Button variant="contained" size="small" fullWidth onClick={onStartTrial}>
                    Start Free Trial
                  </Button>
                </CardContent>
              </BorderLeftCard>
            </Box>

            {/* Activity 2 */}
            <Box sx={{ flex: 1 }}>
              <BorderLeftCard bordercolor={theme.palette.primary.main}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Brain sx={{ color: "primary.main" }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Practice Weak Skills
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Get unlimited practice questions targeting your specific improvement areas
                  </Typography>
                  <Button variant="contained" size="small" fullWidth onClick={onStartTrial}>
                    Start Free Trial
                  </Button>
                </CardContent>
              </BorderLeftCard>
            </Box>

            {/* Activity 3 */}
            <Box sx={{ flex: 1 }}>
              <BorderLeftCard bordercolor={theme.palette.primary.main}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Target sx={{ color: "primary.main" }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Get Personalized Study Plan
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Receive a custom study schedule based on your results to reach your target score
                  </Typography>
                  <Button variant="contained" size="small" fullWidth onClick={onStartTrial}>
                    Start Free Trial
                  </Button>
                </CardContent>
              </BorderLeftCard>
            </Box>
          </Box>
        </Box>

        
      </Container>
    </Box>
  );
};

export default DemoDiagnosticTestResults; 